import axios from 'axios';
import type { AuthResponse, HandoffResponse, UserResponse } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_ENDPOINT ;

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('apps');
      localStorage.removeItem('last_session');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export async function loginApi(credentials: { username: string; password: string; force?: boolean }): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/login', credentials);
  return data;
}

export async function checkTokenApi(token: string): Promise<boolean> {
  try {
    const { data } = await api.post('/auth/check', null, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return !!data.valid;
  } catch {
    return false;
  }
}

export async function forgotPasswordApi(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

export async function resetPasswordApi(forgetToken: string, newPassword: string): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/reset-password', {
    forgetPassTokenUser: forgetToken,
    newPassword,
  });
  return data;
}

export async function expireSessionApi(token: string): Promise<void> {
  await api.put('/auth/expire-session', { tokenSession: token });
}

export async function refreshTokenApi(token: string): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>('/auth/refresh', null, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

/** One-time code to open `appId` (Keycloak sessions): the tokens never travel in the URL. */
export async function createHandoffApi(accessToken: string, refreshToken: string, appId: string): Promise<HandoffResponse> {
  const { data } = await api.post<HandoffResponse>('/auth/handoff', { refresh_token: refreshToken, appId }, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function getUserApi(username: string): Promise<UserResponse> {
  const { data } = await api.get<UserResponse>(`/users/${username}`);
  return data;
}
